import { defineStore } from "pinia";
import api, { authApiInstance } from "@/services/api";
import type { Policy, ROLE, Title, User } from "@/types";
import { getUserPermissions, isAdmin, isReadOnly } from "@/utils/permissionHelper";
import type { Permissions } from "@/types/permissions";
import { obs } from "@/utils/observability";
import { getSelectedCompanyId } from "@/utils/companyContext";



type State = {
  user: User | null;
  isLoading: boolean;
  userTitles?: Title[];
  userRoles: ROLE[];
  users: User[];
  userDataPolices: Policy[];
  isError: boolean;
  companyId: string | null,
  errorMsg: string | null,
};

export const useUserStore = defineStore({
  id: "user",
  state: (): State => {
    // Initialize user from localStorage if available
    const storedUser = localStorage.getItem("user");
    let user = null;
    try {
      user = storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      console.warn('[UserStore] Failed to parse stored user:', error);
      user = null;
    }

    return {
      user,
      isLoading: false,
      userTitles: [] as Title[],
      userRoles: [] as ROLE[],
      users: [] as User[],
      userDataPolices: [] as Policy[],
      isError: false,
      companyId: localStorage.getItem("companyId") as string,
      errorMsg: null,
    };
  },
  actions: {
    getUser: async function () {
      await api
        .get("user")
        .then((res) => {
          this.user = res.data;
          this.userDataPolices = res.data.dataPolicies;
          localStorage.setItem("user", JSON.stringify(res.data));
          localStorage.setItem("dataPolicies", res.data.dataPolicies);

          if (!res.data?.roles?.length) {
            // No roles means no permissions at all: the user can browse but every
            // write action disappears. Never let that pass silently.
            obs.message("user.getUser.noRoles", "error", {
              userId: res.data?.id,
              email: res.data?.email,
              companyId: localStorage.getItem("companyId"),
            });
          }
        })
        .catch((e) => {
          // A swallowed failure here leaves `user` null, which strips every
          // permission-gated affordance for the rest of the session. Reported,
          // but `isError` is left alone: it is shared with the collaborator
          // create/update flows and a boot failure must not poison those.
          obs.captureError(e, "user.getUser", {
            hadCachedUser: this.user !== null,
            companyId: localStorage.getItem("companyId"),
          });
        });
    },
    titles: async function () {
      this.isLoading = true;
      await api
        .get(`user/title`)
        .then((response) => {
          const { data } = response;
          this.userTitles = data;
          this.isLoading = false;
        })
        .catch((e) => {
          console.log("e", e);
          this.isLoading = false;
        });
    },
    async updateUser(userId: string, payload: object) {
      this.isLoading = true;
      await api
        .put(`/user/${userId}`, payload)
        .then((response) => {
          const { data } = response;
          this.isLoading = false;
        })
        .catch((e) => {
          this.isLoading = false;
        });
    },
    async changeUserPassword(payload: object) {
      this.isLoading = true;
      this.isError = false;
      this.errorMsg = null;
      await authApiInstance.
        post(`auth/change-password`, payload)
        .then(() => {
          this.isLoading = false;
        })
        .catch((e: any) => {
          this.isLoading = false;
          this.isError = true;
          this.errorMsg = e;
          console.log(e);
        });
    },
    async updatePassword(payload: object) {
      this.isLoading = true
      this.isError = false
      try {
        await api.put('/user/password', payload)
        this.isLoading = false
        return true
      } catch (e) {
        this.isLoading = false
        this.isError = true
        throw e
      }
    },
    async createUser(payload: object) {
      this.isLoading = true;
      this.isError = false
      try {
        const { data } = await api.post(`user`, payload);
        this.users.push(data);
        this.isLoading = false;
      } catch (e) {
        this.isLoading = false;
        this.isError = true
      }
    },
    async fetchUserRoles() {
      this.isLoading = true;

      try {
        const { data } = await api.get(`role`);
        this.userRoles = <ROLE[]>data;
        this.isLoading = false;
      } catch (e) {

      }
    },
    async fetchUsers() {
      const companyId = getSelectedCompanyId();
      // No company selected — skip to avoid hitting `company/null/user`.
      if (!companyId) {
        obs.breadcrumb("user.fetchUsers.skipped", { reason: "no-company" });
        return;
      }
      this.isLoading = true;
      try {
        const { data } = await api.get(`company/${companyId}/user`);
        this.users = data;
        this.isLoading = false;
      } catch (e) {}
    },
    async deleteUser(userId: string) {
      this.isLoading = true;

      const index = this.users.findIndex((user) => user.id === userId);

      this.users.splice(index, 1);

      await api.delete(`user/${userId}`);
    },
    async updateUserDataPolicy(userId: string) {
      this.isLoading = true;
      try {
        const { data } = await api.put(
          `user/${userId}/data-policy`,
          this.userDataPolices
        );
        this.isLoading = false;
      } catch (e) {
        this.isLoading = false;
      }
    },
  },
  getters: {
    getLocalUser(state) {
      return state.user ? state.user : false;
    },
    getAssignedCompanies(state) {
      return state.user ? state.user.assignedCompanies : [];
    },
    getUserRoles(state): ROLE[] {
      return state.user ? state.user.roles : [];
    },
    
    // ===== Permission System Integration =====
    
    /**
     * Kullanıcının tüm permissionlarını döndürür
     */
    getUserPermissions(state): Permissions[] {
      const userRoles = state.user ? state.user.roles : [];
      return getUserPermissions(userRoles);
    },
    
    /**
     * Kullanıcının admin olup olmadığını kontrol eder
     */
    isUserAdmin(state): boolean {
      const userRoles = state.user ? state.user.roles : [];
      return isAdmin(userRoles);
    },
    
    /**
     * Kullanıcının read-only olup olmadığını kontrol eder  
     */
    isUserReadOnly(state): boolean {
      const userRoles = state.user ? state.user.roles : [];
      return isReadOnly(userRoles);
    },
    
    /**
     * Kullanıcının herhangi bir rolü olup olmadığını kontrol eder
     */
    hasAnyRole(state): boolean {
      return state.user ? state.user.roles.length > 0 : false;
    },
    
    /**
     * Kullanıcının aktif olup olmadığını kontrol eder (user var mı?)
     */
    isAuthenticated(state): boolean {
      return state.user !== null;
    }
  },
});
